import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useIsCompactMode } from '@frontend/responsiveness';
import { SurfaceHeader } from '@frontend/surface-header';
import { useTaskCenterStore } from '@frontend/task-center-scope';
import { theme } from '@frontend/theme';
import { Chip, Tabs, Text, usePopoverDialog } from '@frontend/design-system';
import { TaskFilters } from '../../components';
import { TaskCenterTabs } from '../../constants';
import { taskCenterTrackingIds } from '../../tracking-ids';
import { getAssigneeDetails } from '../../utils';
import { TaskAssigneeFilter } from '../filters/task-assignee-filter/task-assignee-filters';
import { TaskByStatus } from '../task-by-status';
import { containerStyle, tabButtonStyle, taskListContainer } from './task-list.styles';

export const TaskList = () => {
  const { t } = useTranslation('task-center');
  const isCompactMode = useIsCompactMode();
  const {
    taskMode,
    setTaskMode,
    setTab,
    selectedTab,
    assignees,
    assigneeFilter,
    setAssigneeFilter,
    setIsTaskCenterOpen,
  } = useTaskCenterStore([
    'taskMode',
    'setTaskMode',
    'setTab',
    'selectedTab',
    'assignees',
    'assigneeFilter',
    'setAssigneeFilter',
    'setIsTaskCenterOpen',
  ]);

  const { name: assignee, AvatarComponent } = getAssigneeDetails(assignees, assigneeFilter);
  const popoverDialogProps = usePopoverDialog();

  const openCreateTask = () => setTaskMode('create');

  return (
    <div css={[containerStyle, taskMode && { display: 'none' }]}>
      <SurfaceHeader.Root>
        <SurfaceHeader.Row>
          <SurfaceHeader.Start>
            {isCompactMode && (
              <SurfaceHeader.Icon
                iconName='caret-left-small'
                onClick={() => setIsTaskCenterOpen(false)}
                label={t('Back')}
              />
            )}
          </SurfaceHeader.Start>
          <SurfaceHeader.Title>
            {t('Tasks')}
            <span
              css={{ display: 'flex', marginLeft: theme.spacing(1.5), alignItems: 'center', gap: theme.spacing(0.5) }}
            >
              {!isCompactMode && (
                <Text size='medium' weight='regular'>
                  {t('Assigned To:')}
                </Text>
              )}
              <Chip.StatusSwitcher
                variant='neutral'
                // @ts-expect-error Right element prop does not exists in the chip, but is available as it extending Chip
                rightElement={<Icon name='caret-down-small' size={12} />}
                css={css`
                  border: none;
                  max-width: 100%;
                  padding: ${theme.spacing(2.25, 1)};
                `}
                {...popoverDialogProps.getTriggerProps()}
                trackingId={taskCenterTrackingIds.filters.assigneeFilter}
              >
                <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(0.5) }}>
                  {AvatarComponent && <AvatarComponent />}
                  <Text css={{ maxWidth: '90px', overflow: 'hidden', textOverflow: 'ellipsis' }} size='small'>
                    {assignee}
                  </Text>
                </div>
              </Chip.StatusSwitcher>
              {popoverDialogProps.isOpen && (
                <TaskAssigneeFilter
                  setValue={(value: string) => {
                    setAssigneeFilter(value);
                    popoverDialogProps.close();
                  }}
                  selectedValue={assigneeFilter}
                  getDialogProps={popoverDialogProps.getDialogProps}
                />
              )}
            </span>
          </SurfaceHeader.Title>
          <SurfaceHeader.Actions>
            <TaskFilters />
            <SurfaceHeader.Icon
              iconName='draft'
              onClick={openCreateTask}
              trackingId={taskCenterTrackingIds.createTaskDraftButton}
              label={t('Create Task')}
              hoverLabel={t('Create Task')}
            />
            {!isCompactMode && (
              <>
                <SurfaceHeader.Divider />
                <SurfaceHeader.Icon iconName='x' onClick={() => setIsTaskCenterOpen(false)} />
              </>
            )}
          </SurfaceHeader.Actions>
        </SurfaceHeader.Row>
      </SurfaceHeader.Root>
      <Tabs initialTab={selectedTab} controlledTab={selectedTab} onChange={(id) => setTab(id)}>
        <Tabs.Bar>
          {TaskCenterTabs.map(({ title, controller, trackingId }) => (
            <Tabs.Tab
              id={controller}
              css={tabButtonStyle}
              key={`tab-${controller}`}
              trackingId={trackingId}
              aria-label={t('{{tab}} tab', { tab: title })}
            >
              {title}
            </Tabs.Tab>
          ))}
        </Tabs.Bar>
        {TaskCenterTabs.map(({ status, controller }) => (
          <Tabs.Panel controller={controller} key={`tab-panel-${controller}`} css={taskListContainer}>
            <TaskByStatus status={status} />
          </Tabs.Panel>
        ))}
      </Tabs>
    </div>
  );
};
